import {
  Box,
  BoxProps,
  Grid,
  Text,
  useBreakpointValue,
  useTheme,
} from '@chakra-ui/react';
import { camelCase } from 'lodash';
import React, { ReactNode } from 'react';
import { METRICS_NAME } from '~/constants';
import { hexToRgbaString } from '~/utils';

import {
  IHighlightMultiValue,
  MediaPackDetailHighlightCard,
} from '../../MediaPackDetailHighlightCard';
import { IMetricConfigMulti } from '../MediaPackDetailEditHighlightsModal.types';
import { IMetricCardProps } from './MetricCard.types';

type Props = Omit<BoxProps, 'onClick'> & IMetricCardProps<IMetricConfigMulti>;

export const MetricCardMulti: React.FC<Props> = ({
  benchmarks,
  isActive,
  metric,
  metricConfig,
  metricPosition,
  metrics,
  onClick,
  ...props
}) => {
  const { defaultMetricKey, title } = metricConfig;
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const metricsKey = metricPosition.length ? metricPosition : defaultMetricKey;
  const theme = useTheme();

  const { values, descriptions } = metricsKey.reduce(
    (
      acc: {
        values: IHighlightMultiValue[];
        descriptions: { title: string; description: ReactNode }[];
      },
      metricKey,
    ) => {
      const metric = metrics.find(({ key }) => key === metricKey);
      const value = benchmarks[metricKey];
      if (metric && value !== null && value !== undefined) {
        acc.values.push({
          title: metric.title,
          value,
          transformValue: metric.transformValue,
        });
        metric?.description &&
          acc.descriptions.push({
            title: metric.title,
            description: metric.description,
          });
      }
      return acc;
    },
    { values: [], descriptions: [] },
  );

  const highlightCardProps = {
    title,
    values,
    description: descriptions?.length ? (
      <>
        {descriptions.map(({ title, description }) => (
          <Box key={camelCase(title)} _notLast={{ marginBottom: 2 }}>
            <Text fontWeight={500}>{title}:</Text>
            <Text>{description}</Text>
          </Box>
        ))}
      </>
    ) : null,
  };

  const handleClick = () => {
    onClick && onClick(metric);
  };

  return (
    <Grid>
      {!isMobile && (
        <Text
          color="primary.500"
          fontSize="md"
          fontWeight={500}
          marginBottom={3}
        >
          {METRICS_NAME[metric]}:
        </Text>
      )}

      <MediaPackDetailHighlightCard
        data-testid={`${camelCase(metric)}-card`}
        cursor={onClick ? 'pointer' : undefined}
        borderWidth={isActive ? '2px' : '1px'}
        borderColor={isActive ? 'secondary.500' : 'primary.200'}
        backgroundColor={
          isActive
            ? hexToRgbaString(theme.colors.secondary[50], 0.2)
            : undefined
        }
        onClick={handleClick}
        {...highlightCardProps}
        {...props}
      />
    </Grid>
  );
};
